import { useEffect, useState } from "react";
import { previewBody } from "./articles.api";
import type { ArticleBlock } from "./types";

interface PreviewState {
  blocks: unknown[];
  warnings: string[];
  readingMinutes: number;
  error: string | null;
}

const idle: PreviewState = { blocks: [], warnings: [], readingMinutes: 0, error: null };

/** Blocks come from the API's parser. Text is always rendered as text; there is no HTML path. */
function Block({ block }: { block: ArticleBlock }) {
  switch (block.type) {
    case "heading":
      return block.level === 2 ? <h3>{block.text}</h3> : <h4>{block.text}</h4>;
    case "paragraph":
      return <p>{block.text}</p>;
    case "bullets":
      return (
        <ul>
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    case "numbered":
      return (
        <ol>
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      );
    case "quote":
      return <blockquote>{block.text}</blockquote>;
    case "image":
      // Defence in depth: the parser already drops non-https URLs.
      return /^https:\/\//i.test(block.url) ? (
        <img src={block.url} alt={block.alt} referrerPolicy="no-referrer" className="preview-img" />
      ) : null;
    default:
      return null; // a block type this version does not know
  }
}

const isBlock = (b: unknown): b is ArticleBlock =>
  typeof b === "object" && b !== null && typeof (b as { type?: unknown }).type === "string";

export function BodyPreview({ source, delayMs = 500 }: { source: string; delayMs?: number }) {
  const [state, setState] = useState<PreviewState>(idle);

  useEffect(() => {
    if (!source.trim()) {
      setState(idle);
      return;
    }
    let current = true;
    const timer = setTimeout(() => {
      previewBody(source).then(
        (result) => current && setState({ blocks: result.blocks, warnings: result.warnings, readingMinutes: result.readingMinutes, error: null }),
        (err: unknown) =>
          current && setState({ ...idle, error: err instanceof Error ? err.message : "Terjadi kesalahan" }),
      );
    }, delayMs);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [source, delayMs]);

  return (
    <section aria-label="Pratinjau" className="preview">
      {state.error && (
        <p role="alert" className="form-error">
          Pratinjau gagal: {state.error}
        </p>
      )}
      {state.warnings.length > 0 && (
        <ul className="preview-warnings" aria-label="Peringatan parser">
          {state.warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
      {!source.trim() ? (
        <p className="muted">Pratinjau akan muncul di sini saat Anda mengetik isi artikel.</p>
      ) : (
        <>
          {state.readingMinutes > 0 && <p className="muted">≈ {state.readingMinutes} mnt baca</p>}
          {state.blocks.filter(isBlock).map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </>
      )}
    </section>
  );
}
